'use client';

import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { fetchEventTranslation, type EventTranslation } from '@/lib/event-translation';

export interface TranslatedValues {
  title: string;
  titleLocal?: string;
  notes?: string;
  complianceRisk?: string;
}

const EventTranslationContext = createContext<TranslatedValues | null>(null);

export function useEventTranslation(): TranslatedValues {
  const ctx = useContext(EventTranslationContext);
  if (!ctx) throw new Error('useEventTranslation must be used within EventTranslatedContent');
  return ctx;
}

interface EventTranslatedContentProps {
  eventId: string;
  lang: string;
  /** English fallback values (or SSR-resolved translated values) */
  title: string;
  titleLocal?: string;
  notes?: string;
  complianceRisk?: string;
  /** Whether notes are templated (already handled by Tier 1 phrases) */
  notesIsTemplated: boolean;
  /** True if SSR already resolved the translation — skip client-side fetch */
  ssrResolved?: boolean;
  children: ReactNode;
}

/**
 * Client component that fetches on-demand translations for event content.
 * Provides translated values via React context so server-rendered children
 * can consume them through small client components (T, TranslatedSubtitle, etc.).
 */
export function EventTranslatedContent({
  eventId,
  lang,
  title,
  titleLocal,
  notes,
  complianceRisk,
  notesIsTemplated,
  ssrResolved,
  children,
}: EventTranslatedContentProps) {
  const [translation, setTranslation] = useState<EventTranslation | null>(null);

  useEffect(() => {
    // Skip client-side fetch if SSR already resolved the translation
    if (lang === 'en' || ssrResolved) return;

    let cancelled = false;
    fetchEventTranslation(eventId, lang).then(t => {
      if (!cancelled && t) setTranslation(t);
    });
    return () => { cancelled = true; };
  }, [eventId, lang, ssrResolved]);

  const resolved: TranslatedValues = {
    title: translation?.title ?? title,
    titleLocal: translation?.titleLocal ?? titleLocal,
    // For unique notes, use translation; for templated notes, keep the Tier 1 version passed via `notes`
    notes: (!notesIsTemplated && translation?.notes) ? translation.notes : notes,
    complianceRisk: translation?.complianceRisk ?? complianceRisk,
  };

  return (
    <EventTranslationContext.Provider value={resolved}>
      {children}
    </EventTranslationContext.Provider>
  );
}

/** Render a single translated field as inline text */
export function T({ field }: { field: keyof TranslatedValues }) {
  const t = useEventTranslation();
  return <>{t[field] ?? null}</>;
}
